import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  styled,
  useTheme,
  alpha
} from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.customShadows.z16,
    borderColor: alpha(theme.palette.primary.main, 0.2),
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 56,
  height: 56,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  marginBottom: theme.spacing(2),
}));

const ValueTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2rem',
  lineHeight: 1.2,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(0.5),
}));

const TrendBox = styled(Box)<{ trend: 'up' | 'down' | 'neutral' }>(({ theme, trend }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: 
    trend === 'up' ? alpha(theme.palette.success.main, 0.1) :
    trend === 'down' ? alpha(theme.palette.error.main, 0.1) :
    alpha(theme.palette.text.secondary, 0.1),
  color:
    trend === 'up' ? theme.palette.success.main :
    trend === 'down' ? theme.palette.error.main :
    theme.palette.text.secondary,
}));

interface StatCardProps {
  title: string;
  value: string | number;
  icon: SvgIconComponent;
  trend?: {
    direction: 'up' | 'down' | 'neutral';
    value: string;
    label: string;
  };
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  subtitle?: string;
}

export default function StatCard({ 
  title, 
  value, 
  icon: Icon, 
  trend, 
  color = 'primary',
  subtitle 
}: StatCardProps) {
  const theme = useTheme();

  const getColorValue = (colorName: string) => {
    switch (colorName) {
      case 'secondary': return theme.palette.secondary.main;
      case 'success': return theme.palette.success.main;
      case 'warning': return theme.palette.warning.main;
      case 'error': return theme.palette.error.main;
      case 'info': return theme.palette.info.main;
      default: return theme.palette.primary.main;
    }
  };

  return (
    <StyledCard>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              gutterBottom
              sx={{ fontWeight: 500, letterSpacing: 0.5 }}
            >
              {title}
            </Typography>
            
            <ValueTypography
              sx={{
                background: `linear-gradient(135deg, ${getColorValue(color)} 0%, ${alpha(getColorValue(color), 0.7)} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {typeof value === 'number' ? value.toLocaleString() : value}
            </ValueTypography>

            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {subtitle}
              </Typography>
            )}

            {trend && (
              <TrendBox trend={trend.direction}>
                {trend.direction === 'up' ? '↗' : trend.direction === 'down' ? '↘' : '→'}
                <span>{trend.value}</span>
                <Typography variant="caption" sx={{ ml: 0.5, opacity: 0.8 }}>
                  {trend.label}
                </Typography>
              </TrendBox>
            )}
          </Box>

          <StyledAvatar
            sx={{
              background: `linear-gradient(135deg, ${getColorValue(color)} 0%, ${alpha(getColorValue(color), 0.8)} 100%)`,
            }}
          >
            <Icon sx={{ fontSize: 28 }} />
          </StyledAvatar>
        </Box>
      </CardContent>
    </StyledCard>
  );
}
